<template>
	<div class="section">
		<b-loading is-full-page :active.sync="loading"></b-loading>

		<div class="container loading-blur">
			<div class="level">
				<div class="level-left">
					<div class="level-item">
						<img class="flag-large" :src="getFlag(country.LogoPath)" />
						<h1>{{ country.Name }}</h1>
					</div>
				</div>

				<div v-if="country.Score != 0" class="level-right">
					<div class="level-item" v-show="country.Name">
						<a href="javascript://" @click="print()">
							<strong>
								<span v-show="CFTAccess">
									<i class="fad fa-download"></i>
									&nbsp;Download
								</span>
								<span v-show="!CFTAccess">
									<i class="fad fa-shopping-basket"></i>
									&nbsp;Order
								</span>
								CFT Version
							</strong>
						</a>
						&nbsp;
						<div v-show="printing"><i class="far fa-spinner-third fa-spin"></i></div>
					</div>
				</div>
			</div>
			<div v-if="country.IsSpecialNotice">
				{{ country.Notice }}
			</div>
			<div v-if="country.Score == 0">
				<p>
					AWG does not express any views on compliance with CTC, including the primacy and
					completeness of the treaty, in {{ country.Name }}. Substantial caution should be used in
					any reliance on CTC in connection with jurisdictional analysis and transaction risk
					assessment in {{ country.Name }}.
				</p>
				<div v-if="!country.IsSpecialNotice">
					<p>
						{{ country.Name }} will remain unscored until AWG receives input on legal facts from at
						least two law firms, in accordance with the
						<router-link to="/about/methodology">Methodology</router-link>
						.
					</p>
				</div>
				<div class="breathe" v-if="country.IsSpecialNotice && country.AnnotationName != null">
					<h2>{{ country.Name }} Annotations</h2>
					<p>
						<a @click="downloadDocument(country.AnnotationName, false)">Annotations</a>
						<span v-show="downloadingAnnotation"><i class="far fa-spinner-third fa-spin"></i></span>
					</p>
				</div>
			</div>
			<div v-if="country.Score != 0">
				<div v-if="country.Watchlist && CanSeeWatchList">
					<a style="border: none !important" href="#notices">
						<span class="cursor-default" v-tooltip="Tooltip($watchlist)">
							<i class="fad fa-exclamation-triangle"></i>
							On watchlist
						</span>
					</a>
				</div>
				<br />

				<div class="columns">
					<div class="column is-10-desktop is-offset-1-desktop">
						<p class="is-paddingless is-marginless" style="font-style: italic">
							This is not a certified version.
						</p>
						<div class="card has-text-centered card-padding">
							<div class="columns">
								<div class="column">
									<div>
										Made Qualifying Declarations
										<i class="far fa-info-circle"
											 v-tooltip="
		Tooltip(
				'Whether this contracting state has made the required declarations and has not made any prohibited declarations under the OECD Sector Understanding on Export Credits for Civil Aircraft (ASU). For further information, see <a href=\'/about/methodology\'>&quot;Methodology&quot;</a> and <a href=\'http://www.oecd.org/officialdocuments/publicdisplaydocumentpdf/?doclanguage=en&cote=tad/pg(2019)1\'>the ASU (see page 49)</a>'
		)
"
											 ></i>
									</div>
									<p v-if="country.QualifyingDeclarations"><b>Yes</b></p>
									<p v-if="!country.QualifyingDeclarations"><b style="color: red">No</b></p>
								</div>
								<div class="column">
									<div>
										Score
										<i class="far fa-info-circle"
											 		v-tooltip="
		Tooltip(
				'Overall score of this contracting state calculated under the Index formula.'
		)
"
											 ></i>
									</div>
									<p>
										<b>{{ country.Score }}</b>
									</p>
								</div>
								<div class="column">
									<div>
										Category
										<i class="far fa-info-circle"
											 v-tooltip="
		Tooltip(
				'Likelihood of compliance with CTC (very high, high, medium, or low). For further information, see <a href=\'/about/methodology\'>&quot;Methodology&quot;</a>'
		)
"
											 ></i>
									</div>
									<p>
										<b>{{ country.Category }}</b>
									</p>
								</div>
							</div>
							<div class="columns">
								<div class="column">
									<div>
										ASU Eligibility
										<i class="far fa-info-circle"
											 v-tooltip="
		Tooltip('Eligibility for the Cape Town Convention discount under 2011 ASU.')
"
											 ></i>
									</div>
									<p>
										<b>{{ country.ASU }}</b>
									</p>
								</div>
								<div class="column">
									<div>
										Precedent-Based Adjustment
										<i class="far fa-info-circle"
											 v-tooltip="
		Tooltip(
				'Whether and which PBA weight has been applied. When a PBA is applied, it increases the weight of Variable B, and decreases the weights of Variables E and F at levels I, II or III, based on the predictive value of court cases or reported administrative action. Level I corresponds to a low predictive value; Level II corresponds to a medium predictive value; and Level III corresponds to a high predictive value. For further information on PBA, please read &quot;Precedent-Based Adjustment&quot; in <a href=\'/about/methodology\'>&quot;Methodology&quot;</a>.'
		)
"
											 ></i>
									</div>
									<p v-if="country.isPBA">
										<b>Yes - {{ country.PBA | PBA }}</b>
									</p>
									<p v-if="!country.isPBA"><b>No</b></p>
								</div>
								<div class="column">
									<div>
										Variable Result Breakdown
										<i class="far fa-info-circle"
											 v-tooltip="
		Tooltip(
				'<p>Variable A: legal implementation of CTC nationally, in terms of primacy and completeness;</p><p>Variable B: CTC compliance precedents applying CTC, including judicial or administrative precedents and practical experience;</p><p>Variable C: whether a country is and should remain eligible for a \'CTC discount\' under the OECD Aircraft Sector Understanding and whether it has been removed from such eligibility due to non-compliance with CTC;</p><p>Variable D: establishment and efficacy of a \'communications channel\' between the government and AWG to consult on and address CTC compliance issues;</p><p>Variable E: jurisdictional analysis of legal rules and practices, other than those covered by variables A, B, and F, indicating anticipated compliance with CTC;</p><p>Variable F: weighing of select political risk and rule of law indices measuring, or materially relevant to, regulatory enforcement of existing law and protection of property rights.</p><p>For detailed information on each variable, please see <a href=\'/about/methodology\'>&quot;Methodology&quot;</a>.</p>'
		)
"
											 ></i>
									</div>
									<div>
										<div class="columns is-mobile has-text-weight-bold">
											<div class="column">
												<div class="underline">A</div>
												<div>{{ country.A }}</div>
											</div>
											<div class="column">
												<div class="underline">B</div>
												<div>{{ country.B }}</div>
											</div>
											<div class="column">
												<div class="underline">C</div>
												<div>{{ country.C }}</div>
											</div>
											<div class="column">
												<div class="underline">D</div>
												<div>{{ country.D }}</div>
											</div>
											<div class="column">
												<div class="underline">E</div>
												<div>{{ country.E }}</div>
											</div>
											<div class="column">
												<div class="underline">F</div>
												<div>{{ country.F }}</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="breathe" v-show="CanViewScorecard">
					<div v-if="country.ExplanatoryComments">
						<p><b>Select explanatory comments:</b></p>
						<p v-html="country.ExplanatoryComments"></p>
					</div>
					<div v-if="country.ImprovingScore" class="breathe">
						<p><b>Practical Actions that would increase score:</b></p>
						<p v-html="country.ImprovingScore"></p>
					</div>

					<div class="breathe" v-if="AnnotationAccess && country.AnnotationName != null">
						<h2>{{ country.Name }} Annotations</h2>
						<p>
							<a @click="downloadDocument(country.AnnotationName, false)">Annotations</a>
							<span v-show="downloadingAnnotation">
								<i class="far fa-spinner-third fa-spin"></i>
							</span>
						</p>
					</div>
					<div v-if="documents.length > 0 && DocumentAccess" id="notices">
						<h2>
							{{ country.Name }} Notices
							<span v-show="downloadingSupporting">
								<i class="far fa-spinner-third fa-spin"></i>
							</span>
						</h2>
						<div class="breathe">
							<div class="columns">
								<div class="column is-8">
									<b-table
										:data="documents"
										:mobile-cards="true"
										:has-navigation-simple="true"
										default-sort-direction="desc"
										default-sort="Uploaded">
										<b-table-column field="Title" label="Title" sortable v-slot="props">
											<div v-if="DocumentAccess">
												<a @click="downloadDocument(props.row, true)">{{ props.row.Title }}</a>
												<span v-show="props.row.Loading">
													<i class="far fa-spinner-third fa-spin"></i>
												</span>
											</div>
											<div v-if="!DocumentAccess">
												{{ props.row.Title }}
											</div>
										</b-table-column>
										<b-table-column field="Uploaded" label="Date" sortable v-slot="props">
											{{ props.row.Uploaded | utc | moment("M/DD/YYYY") }}
										</b-table-column>
									</b-table>
								</div>
							</div>
						</div>
					</div>
					<div v-if="CanSeeHistoricalScoring" class="breathe">
						<h2>Graph historical data</h2>
						<div class="columns">
							<div class="column is-6" v-if="scoreHistory.datasets">
								<div style="width: 90%">
									<score-chart :chart-data="scoreHistory"></score-chart>
								</div>
							</div>
							<div class="column is-6" v-if="variablesChartdata.datasets">
								<div style="width: 90%">
									<variables-chart :chart-data="variablesChartdata"></variables-chart>
								</div>
							</div>
						</div>
					</div>
					<div v-if="country.Acknowledgements" class="breathe">
						<p><b>Acknowledgements</b></p>
						<p v-html="country.Acknowledgements"></p>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<script>
	import Role from "@/roles";
	import scoreChart from "@/services/components/ScoreChartCountries.vue";
	import variablesChart from "@/services/components/VariablesChart.vue";
	export default {
		components: {
			scoreChart,
			"variables-chart": variablesChart
		},
		props: {
			GroupId: String
		},
		data() {
			return {
				country: {},
				documents: [],
				scoreHistory: {},
				variablesChartdata: {},
				downloadingAnnotation: false,
				printing: false,
				downloadingSupporting: false,
				products: [],
				loading: true
			};
		},
		computed: {
			CFTDownloadId() {
				if (this.products.length > 0) {
					var products = this.products
						.filter((p) => p.Name.includes("CFT Download"))
						.map((p) => p.Id);
					if (products.length == 1) return products[0];
					else return null;
				} else return null;
			},
			CFTAccess() {
				if (this.$store.getters.user) {
					return this.$store.getters.user.Roles.some(
						(r) =>
							r == Role.Admin || r == Role.Enhanced || r == Role.AWGMember || r == Role.CFTPackage
					);
				}
				return false;
			},
			AnnotationAccess() {
				if (
					this.GroupId == "a70fc607-1f3b-4957-8734-134da9cdfda0" ||
					this.GroupId == "e448686f-53d3-47db-8746-ca93107d5ef4" ||
					this.GroupId == "8302d26b-2c4d-417d-ad8b-19cce1f761e4"
				) {
					return true;
				}
				var user = this.$store.getters.user;
				if (user)
					return user.Roles.some(
						(r) =>
							r == Role.Admin ||
							r == Role.AWGMember ||
							r == Role.Enhanced ||
							r == Role.AnnotationAccess ||
							(r == Role.CountryViewer && user.Groups.some((g) => g == this.GroupId)) ||
							(r == Role.CountryRespondent && user.Groups.some((g) => g == this.GroupId))
					);
				else return false;
			},
			DocumentAccess() {
				if (this.GroupId == "a70fc607-1f3b-4957-8734-134da9cdfda0") {
					return true;
				}
				var user = this.$store.getters.user;
				if (user)
					return user.Roles.some(
						(r) =>
							r == Role.Admin ||
							r == Role.AWGMember ||
							r == Role.Enhanced ||
							r == Role.Standard ||
							r == Role.CountryRespondent ||
							(r == Role.CountryViewer && user.Groups.some((g) => g == this.GroupId))
					);
				else return false;
			},
			CanSeeWatchList() {
				var user = this.$store.getters.user;
				if (user) {
					if (
						user.Roles.some(
							(r) =>
								r == Role.Admin ||
								r == Role.AWGMember ||
								r == Role.Enhanced ||
								r == Role.Standard ||
								r == Role.CountryViewer
						)
					) {
						return true;
					} else if (
						user.Roles.some(
							(r) => r == Role.CountryRespondent && user.Groups.some((g) => g == this.GroupId)
						)
					) {
						return true;
					} else {
						return false;
					}
				} else return false;
			},
			CanViewScorecard() {
				if (
					this.GroupId == "a70fc607-1f3b-4957-8734-134da9cdfda0" ||
					this.GroupId == "e448686f-53d3-47db-8746-ca93107d5ef4" ||
					this.GroupId == "8302d26b-2c4d-417d-ad8b-19cce1f761e4"
				) {
					return true;
				}
				var user = this.$store.getters.user;
				if (user) {
					if (
						user.Roles.some(
							(r) =>
								r == Role.Admin ||
								r == Role.AWGMember ||
								r == Role.Enhanced ||
								r == Role.Standard ||
								r == Role.ScoringAccess
						)
					) {
						return true;
					} else if (
						user.Roles.some(
							(r) =>
								(r == Role.CountryRespondent || r == Role.CountryViewer) &&
								user.Groups.some((g) => g == this.GroupId)
						)
					) {
						return true;
					} else {
						return false;
					}
				} else return false;
			},
			CanSeeHistoricalScoring() {
				if (this.GroupId == "a70fc607-1f3b-4957-8734-134da9cdfda0") {
					return true;
				}
				var user = this.$store.getters.user;
				if (user) {
					if (user.Roles.some((r) => r == Role.Admin || r == Role.AWGMember || r == Role.Enhanced)) {
						return true;
					} else if (
						user.Roles.some(
							(r) =>
								(r == Role.CountryRespondent || r == Role.CountryViewer) &&
								user.Groups.some((g) => g == this.GroupId)
						)
					) {
						return true;
					} else {
						return false;
					}
				} else return false;
			}
		},
		methods: {
			getFlag(path) {
				if (!path) return;
				return require("@/assets/images/countryLogos/" + path);
			},
			Tooltip(content) {
				return {
					content: content,
					autoHide: false
				};
			},
			getProducts() {
				this.$http.get("api/product/quicklist").then((resp) => {
					this.products = resp.data;
				});
			},
			async getCountry() {
				await this.$http
					.get("api/group/index/" + this.GroupId)
					.then((resp) => {
						this.country = resp.data;
					})
					.catch((err) => {
						this.$buefy.toast.open("An error happened while getting the country.");
					});
			},
			async getDocuments() {
				//if (!this.$store.getters.isFreeUser) {
				await this.$http
					.post("api/document/all/" + this.GroupId + "?type=1")
					.then((resp) => {
						this.documents = resp.data;
					})
					.catch((err) => {
						this.$buefy.notification.open({
							message: "Could not load Supporting Documents",
							type: "is-danger"
						});
					});
				//}
			},
			async getScoreHistory() {
				await this.$http
					.get("api/group/index/" + this.GroupId + "/scores")
					.then((resp) => {
						//resp.data.sort((a, b) => { return a.Period < b.Period ? -1 : 1 });

						var chartData = {
							labels: resp.data.map((r) => r.Period),
							datasets: [
								{
									label: this.country.Name,
									data: resp.data.map((r) => r.Score),
									fill: false
								}
							]
						};
						this.scoreHistory = chartData;
					})
					.catch((err) => {
						this.$buefy.toast.open("An error happened while getting the score history");
					});
			},
			setupVariablesChart() {
				var chartData = {
					labels: ["A", "B", "C", "D", "E", "F"],
					datasets: [
						{
							label: this.country.Name,
							data: [
								parseFloat(this.country.A || 0),
								parseFloat(this.country.B || 0),
								parseFloat(this.country.C || 0),
								parseFloat(this.country.D || 0),
								parseFloat(this.country.E || 0),
								parseFloat(this.country.F || 0)
							]
						}
					]
				};
				this.variablesChartdata = chartData;
			},
			print() {
				if (this.CFTAccess) {
					this.printing = true;
					this.$http({
						method: "post",
						url: "api/print/scorecard",
						data: { questionnaire: this.country },
						responseType: "arraybuffer"
					})
						.then((resp) => {
							let blob = new Blob([resp.data]);
							var filename = "".concat(this.country.Name, " Scorecard ", this.country.Period, ".pdf");

							if (window.navigator && window.navigator.msSaveOrOpenBlob) {
								window.navigator.msSaveOrOpenBlob(blob, filename);
							} else {
								var link = document.createElement("a");
								link.href = window.URL.createObjectURL(blob);
								link.setAttribute("download", filename);
								document.body.appendChild(link);
								link.click();
								document.body.removeChild(link);
							}
							this.printing = false;
						})
						.catch((err) => {
							console.error(err);
						});
				} else {
					this.$router.push({
						name: "checkout",
						params: { productId: this.CFTDownloadId, updatesRole: true, groupId: this.GroupId }
					});
				}
			},
			downloadDocument(row, supporting) {
				if (!supporting) {
					this.downloadingAnnotation = true;
					var S3Name = row;
					var DocName = this.country.Name + " " + this.country.Period + " Annotations";
				} else {
					this.downloadingSupporting = true;
					var S3Name = row.S3Name;
					var DocName = row.Title;
				}
				var re = /(?:\.([^.]+))?$/;
				this.$http({ method: "get", url: "api/document/" + S3Name, responseType: "arraybuffer" })
					.then((resp) => {
						let blob;

						var link = document.createElement("a");
						if (re.exec(S3Name)[0] == ".pdf") {
							blob = new Blob([resp.data], { type: "application/pdf" });
							link.setAttribute("download", DocName + ".pdf");
						} else if (re.exec(S3Name)[0] == ".doc") {
							blob = new Blob([resp.data], { type: "application/msword" });
							link.setAttribute("download", DocName + ".doc");
						} else if (re.exec(S3Name)[0] == ".docx") {
							blob = new Blob([resp.data], {
								type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
							});
							link.setAttribute("download", DocName + ".docx");
						}

						link.href = window.URL.createObjectURL(blob);

						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);
						this.downloadingAnnotation = false;
						this.downloadingSupporting = false;
					})
					.catch((err) => {
						this.downloadingAnnotation = false;
						this.downloadingSupporting = false;
					});
			}
		},
		async created() {
			await this.getCountry();
			this.getDocuments();
			this.getScoreHistory();
			this.setupVariablesChart();
			this.getProducts();

			this.loading = false;
		}
	};
</script>
